import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import './index.scss';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

interface MdNavbarProps {
  source: string;
  levelRange?: [number, number];
  className?: string;
}
const extractPlainText = (raw: string): string => {
  // 去除 HTML 标签
  const withoutTags = raw.replace(/<[^>]*>/g, '');
  // 去除 Markdown 粗体/斜体符号（** __ * _）
  const withoutMarkdown = withoutTags.replace(/(\*\*|__|\*|_)/g, '');
  // 去除 Markdown 转义反斜杠（如 \[ \] \( \) 等）
  const withoutEscapes = withoutMarkdown.replace(/\\(.)/g, '$1');
  return withoutEscapes.trim();
};

const parseTocItems = (source: string, levelRange: [number, number]) => {
  const headingRegex = /^(#{1,6})[^\S\r\n]+(.*)$/gm;
  // 代码块内也有可能有#
  const codeBlockRegex = /```[\s\S]*?```/g;
  const matches = Array.from(
    source.replace(codeBlockRegex, '\n')
      .matchAll(headingRegex),
  );

  return matches
    .map((match, index) => ({
      id: `heading-${index}`,
      text: extractPlainText(match[2]),
      level: match[1].length,
    }))
    .filter(item =>
      item.level >= levelRange[0] && item.level <= levelRange[1] && item.text,
    );
};

const MdNavbar: React.FC<MdNavbarProps> = ({
  source,
  levelRange = [1, 4],
  className = '',
}) => {
  const [activeId, setActiveId] = useState<string>('');
  const [hoverId, setHoverId] = useState<string>('');
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const observerRef = useRef<IntersectionObserver>();
  const tocRef = useRef<HTMLDivElement>(null);
  const isScrollingFromClick = useRef(false);
  const isInitialLoad = useRef(true);
  const initialHash = useRef<string>('');

  // 为DOM元素添加ID
  useEffect(() => {
    const parsedTocItems = parseTocItems(source, levelRange);
    setTocItems(parsedTocItems);

    // 保存初始hash
    initialHash.current = window.location.hash.replace('#', '');

    setTimeout(() => {
      // 匹配标题
      const headingElements = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
      const headingArray = Array.from(headingElements) as HTMLElement[];

      // 按照在页面中出现的顺序匹配标题
      let tocIndex = 0;
      headingArray.forEach((heading) => {
        if (tocIndex >= parsedTocItems.length) return;

        const headingLevel = parseInt(heading.tagName.substring(1));
        const headingText = heading.textContent?.trim() || '';
        const currentTocItem = parsedTocItems[tocIndex];

        // 匹配条件：level相同，并且文本相同或者都为空
        if (headingLevel === currentTocItem.level) {
          const originalText =
            currentTocItem.text === `heading ${tocIndex + 1}`
              ? ''
              : currentTocItem.text;
          if (headingText === originalText) {
            heading.id = currentTocItem.id;
            tocIndex++;
          }
        }
      });

      // 处理初始 hash
      const hash = initialHash.current;
      const container = document.querySelector('.md-page-container') as HTMLElement;

      const targetItem = parsedTocItems.find((item) => item.id === hash);
      if (hash && targetItem) {
        setActiveId(targetItem.id);
        // 如果有hash，滚动到对应位置
        const targetElement = document.getElementById(hash);
        if (targetElement && container) {
          const offsetTop = targetElement.offsetTop - 80;
          container?.scrollTo({
            top: offsetTop,
            behavior: 'smooth',
          });
        }
      } else if (parsedTocItems.length > 0) {
        // 如果没有 hash，默认激活第一个
        setActiveId(parsedTocItems[0].id);
        container?.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }

      // 延迟启动观察器，确保初始位置设置完成
      setTimeout(() => {
        isInitialLoad.current = false;
      }, 1000); // 给足够时间让初始滚动完成
    }, 100);
  }, [source, levelRange]);

  // 设置 Intersection Observer
  useEffect(() => {
    if (tocItems.length === 0) return;

    // 清理之前的观察器
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        // 如果是点击滚动或初始加载，暂时不更新
        if (isScrollingFromClick.current || isInitialLoad.current) return;

        // 获取滚动容器信息
        const container = document.querySelector('.md-page-container') as HTMLElement;
        if (!container) return;

        const { scrollTop, scrollHeight, clientHeight } = container;

        // 优先处理滚动到顶部的情况
        if (scrollTop <= 50) { // 滚动到顶部时
          if (tocItems.length > 0 && activeId !== tocItems[0].id) {
            setActiveId(tocItems[0].id);
          }
          return;
        }

        // 处理滚动到底部的情况
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
        if (isAtBottom) {
          const lastItem = tocItems[tocItems.length - 1];
          if (lastItem.id !== activeId) {
            setActiveId(lastItem.id);
          }
          return;
        }

        // 处理中间区域的滚动
        const intersectingEntries = entries.filter(entry => entry.isIntersecting);

        if (intersectingEntries.length > 0) {
          // 按照在页面中的位置排序，选择最靠近顶部的
          intersectingEntries.sort((a, b) => {
            return a.boundingClientRect.top - b.boundingClientRect.top;
          });

          const topEntry = intersectingEntries[0];
          const newActiveId = topEntry.target.id;

          if (newActiveId !== activeId) {
            setActiveId(newActiveId);
          }
        }
      },
      {
        root: document.querySelector('.md-page-container'),
        // 调整 rootMargin 使检测更合理
        rootMargin: '-10% 0% -80% 0%',
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      },
    );

    // 观察所有标题元素
    tocItems.forEach(item => {
      const element = document.getElementById(item.id);
      if (element) {
        observerRef.current?.observe(element);
      }
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [tocItems, activeId]);

  // 点击跳转
  const scrollToHeading = (id: string) => {
    const element = document.getElementById(id);
    if (!element) return;

    // 设置点击滚动标记
    isScrollingFromClick.current = true;

    // 立即更新状态
    setActiveId(id);

    const container = document.querySelector('.md-page-container') as HTMLElement;
    const offsetTop = element.offsetTop - 20; // 顶部偏移

    if (container) {
      container?.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }

    // 更新URL
    window.history.replaceState(null, '', `#${id}`);

    // 重置点击滚动标记
    setTimeout(() => {
      isScrollingFromClick.current = false;
    }, 1000); // 给足够时间让滚动完成
  };

  // 计算指示器位置
  const getIndicatorStyle = (targetId?: string) => {
    const targetElementId = targetId || activeId;
    if (!targetElementId || !tocRef.current) return { display: 'none' };

    const targetLink = tocRef.current.querySelector(`[data-id="${targetElementId}"]`) as HTMLElement;
    if (!targetLink) return { display: 'none' };

    const containerRect = tocRef.current.getBoundingClientRect();
    const linkRect = targetLink.getBoundingClientRect();

    return {
      top: linkRect.top - containerRect.top,
      height: linkRect.height,
      opacity: 1,
    };
  };

  const handleMouseEnter = (id: string) => {
    setHoverId(id);
  };

  const handleMouseLeave = () => {
    setHoverId('');
  };

  if (tocItems.length === 0) return null;

  return (
    <>
      <p className="title">On this page</p>
      <div className={classNames('odc-toc', className)} ref={tocRef}>
        <div className="toc-track" />
        <div
          className={classNames('toc-indicator toc-indicator-active', {
            'no-transition': isInitialLoad.current,
          })}
          style={getIndicatorStyle(activeId)}
        />
        {hoverId && hoverId !== activeId && (
          <div
            className="toc-indicator toc-indicator-hover"
            style={getIndicatorStyle(hoverId)}
          />
        )}
        <nav className="toc-nav">
          {tocItems.map((item) => (
            <a
              key={item.id}
              data-id={item.id}
              data-level={item.level}
              className={classNames('toc-link', {
                active: activeId === item.id,
                hover: hoverId === item.id,
                'no-transition': isInitialLoad.current,
              })}
              onMouseEnter={() => handleMouseEnter(item.id)}
              onMouseLeave={handleMouseLeave}
              onClick={(e) => {
                e.preventDefault();
                scrollToHeading(item.id);
              }}
              href={`#${item.id}`}
              title={item.text}
            >
              {item.text}
            </a>
          ))}
        </nav>
      </div>
    </>
  );
};

export default MdNavbar;